import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AutoCompleteField, { Option, showAllFilterOption } from '../core/components/Fields/AutoCompleteField.tsx';
import useForceSelection from '../document/components/useForceSelection.ts';
import {
    renderAddressOption,
    renderArticleIdOption,
    renderClientOption,
    renderContactOption,
    renderFrameworkContractOption,
} from '../document/components/useLookup.tsx';
import { useDebounce } from '../core/utils/hooks/useDebounce.ts';

interface Props {
    value: string;
    onValueChange: (value: string) => void;
    /**
     * Called when the user picks an option from the dropdown. The full option
     * includes the raw masterdata entity so the caller can pull things like
     * `companyName`, `articleDescription`, etc.
     */
    onSelect?: (option: Option) => void;

    lookupFieldName: string;
    lookupType?: string;

    disabled?: boolean;
    readOnly?: boolean;
    required?: boolean;
    placeholder?: string;

    /**
     * When false, the field enforces that only values selected from the
     * dropdown are accepted — typing a free-text value that doesn't match
     * an option is cleared on blur.
     */
    allowInvalidIds?: boolean;

    /** Fetches autocomplete options for the given search term. */
    fetchOptions: (searchTerm: string) => Promise<Option[]>;

    minSearchChars?: number;
}

const renderOptionFor = (lookupType: string | undefined, lookupFieldName: string) => {
    switch (lookupType) {
        case 'ClientLookupType':
            return renderClientOption;
        case 'ContactLookupType':
            return renderContactOption;
        case 'ArticleLookupType':
            return renderArticleIdOption(lookupFieldName);
        case 'AddressLookupType':
            return renderAddressOption;
        case 'FrameworkContractLookupType':
            return renderFrameworkContractOption;
        default:
            return renderClientOption;
    }
};

const LookupField = ({
    value,
    onValueChange,
    onSelect,
    lookupFieldName,
    lookupType,
    disabled,
    readOnly,
    required,
    placeholder,
    allowInvalidIds = true,
    fetchOptions,
    minSearchChars = 1,
}: Props) => {
    const { t } = useTranslation(['assistance', 'forms']);
    const [options, setOptions] = useState<Option[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const notEnoughChars = (inputValue || '').length < minSearchChars;

    const doFetch = useCallback(
        async (searchTerm: string) => {
            setIsLoading(true);
            try {
                const opts = await fetchOptions(searchTerm);
                setOptions(opts);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchOptions]
    );

    const debouncedFetch = useDebounce(doFetch, 300);

    useEffect(() => {
        if (notEnoughChars) {
            setOptions([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        debouncedFetch(inputValue || '');
    }, [debouncedFetch, inputValue, notEnoughChars]);

    const forceSelectionProps = useForceSelection({
        value,
        onValueChange: onValueChange,
        onSelectOption: onSelect,
        forceSelection: !allowInvalidIds,
        options,
    });

    return (
        <AutoCompleteField
            value={forceSelectionProps.value}
            onValueChange={(value) => {
                forceSelectionProps.onValueChange(value);
                setInputValue(value || '');
            }}
            onSelectOption={forceSelectionProps.onSelectOption}
            onFocus={forceSelectionProps.onFocus}
            onBlur={forceSelectionProps.onBlur}
            options={options}
            isLoading={isLoading}
            emptyMessage={
                notEnoughChars ? t('forms:lookup.startTyping') : t('assistance:fields.autocompleteField.noResults')
            }
            filterOption={showAllFilterOption}
            renderOption={renderOptionFor(lookupType, lookupFieldName)}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            placeholder={placeholder}
        />
    );
};

export default LookupField;
